import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Chip
} from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import dayjs from 'dayjs';

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL
import { API_URL } from '../utils/apiConfig';

const UpdateOfferLetter = ({ employee, locations, onClose, onSuccess }) => {
  const { userRole } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    position: employee.position || '',
    payRate: employee.payRate || '',
    hireDate: employee.hireDate ? dayjs(employee.hireDate).format('YYYY-MM-DD') : '',
    locationId: employee.locationId || ''
  });
  
  const [originalData] = useState({ ...formData });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [changes, setChanges] = useState([]);

  // Check if user can update offer letters
  const canUpdate = userRole === 'admin' || userRole === 'manager';

  useEffect(() => {
    // Track changes
    const changesList = [];
    
    if (formData.position !== originalData.position && formData.position) {
      changesList.push(`Position: ${originalData.position || 'Not set'} → ${formData.position}`);
    }
    
    if (parseFloat(formData.payRate) !== parseFloat(originalData.payRate) && formData.payRate) {
      changesList.push(`Pay Rate: $${originalData.payRate || '0'} → $${formData.payRate}`);
    }
    
    if (formData.hireDate !== originalData.hireDate && formData.hireDate) {
      const oldDate = originalData.hireDate ? dayjs(originalData.hireDate).format('MM/DD/YYYY') : 'Not set';
      const newDate = dayjs(formData.hireDate).format('MM/DD/YYYY');
      changesList.push(`Hire Date: ${oldDate} → ${newDate}`);
    }
    
    if (formData.locationId !== originalData.locationId && formData.locationId) {
      const oldLocation = locations.find(l => l.id === originalData.locationId)?.name || 'Not set';
      const newLocation = locations.find(l => l.id === formData.locationId)?.name || 'Unknown';
      changesList.push(`Location: ${oldLocation} → ${newLocation}`);
    }
    
    setChanges(changesList);
  }, [formData, originalData, locations]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (changes.length === 0) {
      setError('No changes detected. Please modify at least one field.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/employees/${employee.id}/offer-letter`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update offer letter');
      }

      // Trigger localStorage event to notify offer letter form
      localStorage.setItem('offerLetterUpdated', JSON.stringify({
        employeeId: employee.id,
        timestamp: new Date().getTime()
      }));

      // Also dispatch a custom event for same-window updates
      window.dispatchEvent(new CustomEvent('offerLetterUpdated', {
        detail: {
          employeeId: employee.id,
          timestamp: new Date().getTime()
        }
      }));

      onSuccess({
        ...data,
        employeeId: employee.id,
        employeeName: `${employee.firstName} ${employee.lastName}`
      });
      onClose();
    } catch (err) {
      console.error('Error updating offer letter:', err);
      setError(err.message || 'Failed to update offer letter');
    } finally {
      setLoading(false);
    }
  };

  if (!canUpdate) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Alert severity="error">
            You don't have permission to update offer letters.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EditIcon color="primary" />
          Update Offer Letter for {employee.firstName} {employee.lastName}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Updating the offer letter will generate a new PDF with the updated information. 
          {employee.last_login && (
            <strong> The employee will be notified via email about this change.</strong>
          )}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Position"
              name="position"
              value={formData.position}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Pay Rate ($/hr)"
              name="payRate"
              type="number"
              inputProps={{ step: '0.01', min: '0' }}
              value={formData.payRate}
              onChange={handleChange}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Hire Date"
              name="hireDate"
              type="date"
              value={formData.hireDate}
              onChange={handleChange}
              disabled={loading}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Location</InputLabel>
              <Select
                name="locationId"
                value={formData.locationId}
                onChange={handleChange}
                disabled={loading}
                label="Location"
              >
                {locations.map(location => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {changes.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Changes to be made:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {changes.map((change, index) => (
                <Chip 
                  key={index} 
                  label={change} 
                  color="primary" 
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={onClose} 
          disabled={loading}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || changes.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {loading ? 'Updating...' : 'Update & Regenerate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateOfferLetter;